import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  MessageCircle, ShieldCheck, Clock, Wrench, Sparkles, LogIn, UserCircle2, CreditCard, QrCode, Upload, Zap,
} from "lucide-react";

const DEFAULT_WPP = "5511995708442";
const DEFAULT_MSG =
  "Olá, vim pelo site BBH, gostaria de um orçamento...";

// Normaliza um número brasileiro para o formato E.164 sem "+" (aceito por wa.me).
// - Remove tudo que não é dígito
// - Prepend "55" quando faltar o país
// - Prepend "5511" quando também faltar o DDD
const composeBrWpp = (v) => {
  const digits = (v || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("55") && digits.length >= 12) return digits;
  if (digits.length >= 10) return `55${digits}`;
  return `5511${digits}`;
};

const services = [
  {
    icon: Wrench,
    title: "Manutenção predial",
    desc: "Reparos elétricos, hidráulicos, pintura, alvenaria e mais — atendimento rápido para condomínios e comércios.",
  },
  {
    icon: Sparkles,
    title: "Serviços especializados",
    desc: "Instalações, adequações e projetos sob medida com equipe própria e materiais de qualidade.",
  },
  {
    icon: ShieldCheck,
    title: "Garantia real",
    desc: "Todo serviço acompanha garantia. Você recebe um orçamento formal e acompanha o status pelo celular.",
  },
  {
    icon: Clock,
    title: "Resposta ágil",
    desc: "Solicitou um orçamento? Você recebe em poucas horas, com fotos, valores e condições comerciais.",
  },
];

export default function Landing() {
  const [cfg, setCfg] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const nav = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    api.get("/configuracoes").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/portfolio").then(({ data }) => setPortfolio(data)).catch(() => {});
  }, []);

  // Se o admin já está logado, mandar direto pro painel
  useEffect(() => {
    if (!loading && user?.is_admin) nav("/admin", { replace: true });
  }, [loading, user, nav]);

  const wpp = composeBrWpp(cfg?.whatsapp) || DEFAULT_WPP;
  const nome = cfg?.nome_empresa || "BBH Serviços";
  const logoPath = cfg?.logo ? cfg.logo.replace("/api/files/", "") : null;

  const contratar = () => {
    const url = `https://wa.me/${wpp}?text=${encodeURIComponent(DEFAULT_MSG)}`;
    window.open(url, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-zinc-900" data-testid="landing-page">
      {/* Nav */}
      <header
        className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-zinc-200"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logoPath ? (
              <img src={fileUrl(logoPath)} alt="" className="w-10 h-10 object-contain" />
            ) : (
              <div className="w-10 h-10 rounded-md bg-[#14243D] text-white flex items-center justify-center font-heading font-bold">B</div>
            )}
            <div className="leading-tight">
              <div className="font-heading font-bold">{nome}</div>
              <div className="text-[10px] tracking-widest text-zinc-500 uppercase">Manutenção & Serviços</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={contratar}
              data-testid="header-cta-btn"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
            >
              <MessageCircle className="w-4 h-4" /> Contratar pelo WhatsApp
            </button>
            <Link
              to="/portal/login"
              data-testid="landing-portal-link"
              title="Portal do cliente"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-[#14243D] text-white text-xs font-semibold hover:bg-[#0f1a2e]"
            >
              <UserCircle2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Área do cliente</span>
              <span className="sm:hidden">Cliente</span>
            </Link>
            <Link
              to="/login"
              data-testid="landing-login-link"
              title="Área do administrador"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-zinc-200 text-zinc-500 text-xs font-medium hover:bg-zinc-50"
            >
              <LogIn className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Admin</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 space-y-6">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-[#14243D]">
            Manutenção · Reformas · Serviços
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Serviços rápidos e transparentes para o seu imóvel.
          </h1>
          <p className="text-lg text-zinc-600 max-w-2xl">
            A {nome} atende com equipe própria e envia orçamentos formais direto no seu WhatsApp — com fotos, valores, condições e prazo. Você aprova pelo celular e <strong>agora paga pelo próprio app</strong> via PIX.
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-100 border border-zinc-200 text-zinc-500 text-xs font-semibold" data-testid="badge-cartao-implantacao">
              <CreditCard className="w-3.5 h-3.5" /> Cartão em implantação
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sky-50 border border-sky-200 text-sky-800 text-xs font-semibold">
              <QrCode className="w-3.5 h-3.5" /> PIX com QR Code
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold">
              <Zap className="w-3.5 h-3.5" /> Pague por item ou tudo de uma vez
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={contratar}
              data-testid="hero-cta-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-base font-semibold min-h-[56px]"
            >
              <MessageCircle className="w-5 h-5" /> Solicitar orçamento pelo WhatsApp
            </button>
          </div>
        </div>

        {/* Illustration card / vídeo de apresentação */}
        <div className="lg:col-span-5">
          {cfg?.video_apresentacao ? (
            <div className="bbh-card p-0 relative overflow-hidden" data-testid="landing-video-apresentacao">
              <video
                src={fileUrl((cfg.video_apresentacao || "").replace("/api/files/", ""))}
                controls
                playsInline
                autoPlay
                muted
                loop
                className="w-full aspect-[4/5] sm:aspect-video object-cover bg-black"
              />
              <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/60 text-white text-[10px] font-semibold uppercase tracking-widest">
                Apresentação
              </div>
            </div>
          ) : (
            <div className="bbh-card p-6 sm:p-8 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#14243D]/5" />
            <div className="absolute -bottom-20 -left-16 w-64 h-64 rounded-full bg-amber-100/50" />
            <div className="relative">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-3">
                Como funciona
              </div>
              <ol className="space-y-4">
                {[
                  "Você entra em contato pelo WhatsApp",
                  "Combinamos a visita técnica se necessário",
                  "Enviamos o orçamento com link exclusivo",
                  "Você aprova pelo celular, sem cadastro",
                  "Pague pelo app via PIX",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#14243D] text-white flex items-center justify-center text-xs font-bold shrink-0">
                      {i + 1}
                    </div>
                    <div className="text-sm text-zinc-700 pt-0.5">{t}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
          )}
        </div>
      </section>

      {/* Serviços */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">
            O que fazemos
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
            Serviços para condomínios, comércios e residências
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.map((s, i) => (
            <div key={i} className="bbh-card p-6 hover:shadow-md transition-shadow" data-testid={`service-card-${i}`}>
              <div className="w-11 h-11 rounded-md bg-[#14243D] text-white flex items-center justify-center mb-4">
                <s.icon className="w-5 h-5" />
              </div>
              <div className="font-heading font-semibold text-lg mb-2">{s.title}</div>
              <p className="text-sm text-zinc-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pagamento online */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="landing-pagamento-section">
        <div className="rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #14243D 0%, #1e3a5f 100%)" }}>
          <div className="grid lg:grid-cols-12 gap-8 p-8 sm:p-12 text-white">
            <div className="lg:col-span-5 space-y-4">
              <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "#FBBF24" }}>
                Novidade
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                Agora você paga direto pela plataforma.
              </h2>
              <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
                Após o orçamento aprovado, um botão <strong>Pagar</strong> aparece no link do cliente. Pague via PIX com QR Code e envie o comprovante pelo app. Pagamento por cartão em implantação. Dá pra pagar o valor total ou <strong>por item concluído</strong>.
              </p>
              <div className="flex items-center gap-2 pt-2">
                <ShieldCheck className="w-4 h-4" style={{ color: "#86EFAC" }} />
                <span className="text-xs" style={{ color: "rgba(255,255,255,0.75)" }}>
                  Processamento seguro — a BBH recebe o valor integral da mão de obra.
                </span>
              </div>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-3 gap-3" data-testid="pagamento-metodos-grid">
              {[
                { icon: CreditCard, title: "Cartão de crédito", desc: "Em implantação — em breve.", testid: "metodo-cartao" },
                { icon: QrCode, title: "PIX com QR Code", desc: "Pagamento instantâneo, sem acréscimo.", testid: "metodo-pix" },
                { icon: Upload, title: "Comprovante no app", desc: "Envie o comprovante do PIX e a BBH confirma.", testid: "metodo-comprovante" },
              ].map((m, i) => (
                <div
                  key={i}
                  className="rounded-xl p-5 flex flex-col gap-2"
                  style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)" }}
                  data-testid={m.testid}
                >
                  <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "#FBBF24", color: "#14243D" }}>
                    <m.icon className="w-5 h-5" />
                  </div>
                  <div className="font-heading font-semibold">{m.title}</div>
                  <div className="text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>{m.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Portfólio */}
      {portfolio.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="portfolio-section">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">
              Nosso portfólio
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
              Trabalhos realizados pela BBH
            </h2>
            <p className="text-sm text-zinc-600 mt-3">
              Uma amostra dos nossos serviços recentes — qualidade que fala por si.
            </p>
          </div>
          <PortfolioGrid items={portfolio} />
        </section>
      )}

      {/* CTA final */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden bg-[#14243D] text-white">
          <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
            backgroundImage: "radial-gradient(circle at 20% 20%, #FBBF24 0, transparent 40%), radial-gradient(circle at 80% 80%, #FBBF24 0, transparent 40%)",
          }} />
          <div className="relative">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">Vamos começar seu orçamento?</h2>
            <p className="text-white/70 max-w-xl mx-auto mb-6">
              Chame agora pelo WhatsApp. Uma mensagem já vem pronta com "Olá, vim pelo site BBH".
            </p>
            <button
              onClick={contratar}
              data-testid="final-cta-btn"
              className="inline-flex items-center gap-2 px-6 py-4 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-base font-semibold min-h-[56px]"
            >
              <MessageCircle className="w-5 h-5" /> Contratar a BBH
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-500">
          <div>© {new Date().getFullYear()} {nome}. Todos os direitos reservados.</div>
          {cfg?.email || cfg?.telefone ? (
            <div className="flex gap-3">
              {cfg?.email && <a href={`mailto:${cfg.email}`} className="hover:text-zinc-900">{cfg.email}</a>}
              {cfg?.telefone && <span>{cfg.telefone}</span>}
            </div>
          ) : null}
        </div>
      </footer>
    </div>
  );
}

const PAGE_SIZE = 9;
const isVideoItem = (p) => p?.tipo === "video" || /\.(mp4|mov|webm|m4v)(\?|$)/i.test(p?.foto || "");

function PortfolioGrid({ items }) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [lightbox, setLightbox] = useState(null); // index or null

  const shown = items.slice(0, visibleCount);
  const restante = items.length - visibleCount;
  const podeMostrarMais = restante > 0;

  const mostrarMais = () => setVisibleCount((v) => Math.min(v + PAGE_SIZE, items.length));
  const mostrarMenos = () => setVisibleCount(PAGE_SIZE);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => Math.min(items.length - 1, (i ?? 0) + 1));
      if (e.key === "ArrowLeft") setLightbox((i) => Math.max(0, (i ?? 0) - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, items.length]);

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
        {shown.map((p, idx) => {
          const path = (p.foto || "").replace("/api/files/", "");
          const globalIdx = items.findIndex((it) => it.id === p.id);
          const vid = isVideoItem(p);
          return (
            <button
              type="button"
              key={p.id}
              onClick={() => setLightbox(globalIdx)}
              data-testid={`portfolio-item-${p.id}`}
              className="group relative aspect-square overflow-hidden rounded-md bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#14243D] cursor-zoom-in"
              aria-label={p.observacao ? `Ampliar: ${p.observacao}` : (vid ? "Reproduzir vídeo" : "Ampliar foto")}
            >
              {vid ? (
                <>
                  <video
                    src={fileUrl(path)}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    muted
                    playsInline
                    preload="metadata"
                  />
                  <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black/60 group-hover:bg-black/80 flex items-center justify-center transition-colors">
                      <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                    </span>
                  </span>
                </>
              ) : (
                <img
                  src={fileUrl(path)}
                  alt={p.observacao || ""}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
              )}
              {p.observacao && (
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-white text-[10px] sm:text-xs line-clamp-2 text-left opacity-0 group-hover:opacity-100 transition-opacity">
                  {p.observacao}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {(podeMostrarMais || visibleCount > PAGE_SIZE) && (
        <div className="flex items-center justify-center gap-3 mt-6">
          {podeMostrarMais && (
            <button
              type="button"
              onClick={mostrarMais}
              data-testid="portfolio-mostrar-mais"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#14243D] hover:bg-[#0f1a2e] text-white text-sm font-semibold"
            >
              Mostrar mais ({restante})
            </button>
          )}
          {visibleCount > PAGE_SIZE && (
            <button
              type="button"
              onClick={mostrarMenos}
              data-testid="portfolio-mostrar-menos"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white border border-zinc-200 text-sm font-medium hover:bg-zinc-50"
            >
              Mostrar menos
            </button>
          )}
        </div>
      )}

      {lightbox !== null && (
        <PortfolioLightbox
          items={items}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onPrev={() => setLightbox((i) => Math.max(0, (i ?? 0) - 1))}
          onNext={() => setLightbox((i) => Math.min(items.length - 1, (i ?? 0) + 1))}
        />
      )}
    </>
  );
}

function PortfolioLightbox({ items, index, onClose, onPrev, onNext }) {
  const item = items[index];
  if (!item) return null;
  const path = (item.foto || "").replace("/api/files/", "");
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="portfolio-lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="portfolio-lightbox-close"
        aria-label="Fechar"
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xl font-bold"
      >
        ×
      </button>
      {hasPrev && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          data-testid="portfolio-lightbox-prev"
          aria-label="Anterior"
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-2xl"
        >
          ‹
        </button>
      )}
      {hasNext && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          data-testid="portfolio-lightbox-next"
          aria-label="Próxima"
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-2xl"
        >
          ›
        </button>
      )}
      <figure
        className="max-w-5xl w-full max-h-full flex flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        {isVideoItem(item) ? (
          <video
            src={fileUrl(path)}
            controls
            autoPlay
            playsInline
            className="max-h-[80vh] w-auto max-w-full object-contain rounded-md bg-black"
          />
        ) : (
          <img
            src={fileUrl(path)}
            alt={item.observacao || ""}
            className="max-h-[80vh] w-auto object-contain rounded-md"
          />
        )}
        {item.observacao && (
          <figcaption className="max-w-2xl text-center text-white/90 text-sm whitespace-pre-wrap">
            {item.observacao}
          </figcaption>
        )}
        <div className="text-white/60 text-xs">
          {index + 1} de {items.length}
        </div>
      </figure>
    </div>
  );
}
